<template>
  <Modal
    id="modal_connexion"
    :modal-class="modalClass"
    @before-open="reset($event)"
  >
    <template #title>
      {{ modalTitle }}
    </template>
    <template #subtitle>
      <p class="text-regular modal-connexion-subtitle">
        {{ modalSubtitle }}
      </p>
    </template>
    <template #content>
      <!-- Tab 0 : Choix connexion / demande de compte -->
      <ModalTab v-show="activeTab === 0">
        <template #content>
          <div class="connexion-choices">
            <div class="choice-bloc">
              <p class="s5 bold">
                {{ $t('connexion.jai-un-compte') }}
              </p>
              <p class="text-regular">
                {{ $t('connexion.jai-un-compte-desc') }}
              </p>
              <ButtonClassic
                variant="solid"
                color="primary"
                :label="$t('connexion.me-connecter')"
                icon="right"
                @click="goToLogin()"
              >
                <template #right-icon>
                  <UilArrowRight />
                </template>
              </ButtonClassic>
            </div>

            <hr class="vertical-separator" />

            <div class="choice-bloc">
              <p class="s5 bold">
                {{ $t('connexion.pas-encore-de-compte') }}
              </p>
              <p class="text-regular">
                {{ $t('connexion.pas-encore-de-compte-desc') }}
              </p>
              <ButtonClassic
                variant="solid"
                color="secondary"
                :label="$t('connexion.creer-un-compte')"
                icon="right"
                @click="setTab(1)"
              >
                <template #right-icon>
                  <UilArrowRight />
                </template>
              </ButtonClassic>
            </div>
          </div>
        </template>
      </ModalTab>

      <!-- Tab 1 : Choix du type de demande -->
      <ModalTab v-show="activeTab === 1">
        <template #content>
          <div class="profil-groups">
            <section class="profil-group">
              <p class="s5 bold profil-group-title">
                {{ isExport ? paysLibelle : $t("connexion.profil-categorie-france") }}
              </p>
              <div class="profil-cards">
                <button
                  v-for="profil in profilsFrance"
                  :key="profil.id"
                  type="button"
                  class="profil-card"
                  @click="selectProfil(profil)"
                >
                  <span
                    class="profil-card-icon"
                    :class="`profil-card-icon--${profil.id}`"
                  >
                    <component
                      :is="profil.icon"
                    />
                  </span>
                  <p class="text-medium bold">
                    {{ profil.label }}
                  </p>
                </button>
              </div>
            </section>

            <section
              v-if="!isExport"
              class="profil-group"
            >
              <p class="s5 bold profil-group-title">
                {{ $t("connexion.profil-categorie-hors-france") }}
              </p>
              <div class="profil-cards profil-cards--single">
                <button
                  v-for="profil in profilsHorsFrance"
                  :key="profil.id"
                  type="button"
                  class="profil-card"
                  @click="selectProfil(profil)"
                >
                  <span
                    class="profil-card-icon"
                    :class="`profil-card-icon--${profil.id}`"
                  >
                    <component
                      :is="profil.icon"
                    />
                  </span>
                  <p class="text-medium bold">
                    {{ profil.label }}
                  </p>
                </button>
              </div>
            </section>
          </div>
        </template>
      </ModalTab>

      <!-- Tab 2 : Civilité -->
      <ModalTab v-show="activeTab === 2">
        <template #content>
          <div class="demande-form">
            <SelectClassic
              v-model="selectedSalutationOption"
              :label="$t('connexion.champ-salutation')"
              :inline="false"
              status="required"
              :placeholder="$t('general.placeholder-selectionner')"
              :options="salutationOptions"
            />

            <InputClassic
              :model-value="form.nom"
              :label="$t('connexion.champ-nom')"
              type="text"
              status="required"
              @update:model-value="form.nom = $event"
            />

            <InputClassic
              :model-value="form.prenom"
              :label="$t('connexion.champ-prenom')"
              type="text"
              status="required"
              @update:model-value="form.prenom = $event"
            />

            <InputClassic
              :model-value="form.email"
              :label="$t('general.email')"
              type="email"
              status="required"
              @update:model-value="onEmailInput($event)"
            />

            <InputClassic
              v-if="!hasDemandeDevisContext"
              :model-value="form.motif"
              :label="$t('connexion.champ-motif')"
              :placeholder="$t('connexion.placeholder-motif-demande-compte')"
              type="textarea"
              status="required"
              @update:model-value="form.motif = $event"
            />

            <div class="readonly-field">
              <label class="text-medium">{{ $t('connexion.champ-fonction') }}</label>
              <p class="text-regular">
                {{ selectedProfil ? selectedProfil.label : (form.fonction || '-') }}
              </p>
            </div>

            <div
              v-if="civiliteValidationIssues.length"
              class="civilite-validation-box"
            >
              <p class="text-small bold validation-title">
                {{ $t('connexion.validation-civilite-title') }}
              </p>
              <ul
                class="text-small validation-list"
              >
                <li
                  v-for="(issue, index) in civiliteValidationIssues"
                  :key="`civilite-issue-${index}`"
                >
                  {{ issue }}
                </li>
              </ul>
            </div>
          </div>
        </template>
      </ModalTab>

      <!-- Tab 3 : Établissement -->
      <ModalTab v-show="activeTab === 3 && needsEtablissement">
        <template #content>
          <div class="demande-form">
            <template v-if="!isManualEtablissement">
              <SelectClassic
                v-model="selectedEtablissementOption"
                :label="$t('general.etablissement')"
                :inline="false"
                status="required"
                :internal-search="false"
                :placeholder="$t('connexion.placeholder-etablissement-recherche-3-caracteres')"
                :options="etablissementOptions"
                :options-are-loading="isSearchingEtablissements"
                select-on-enter
                @search="onEtablissementSearchInput"
              >
                <template #label-icon>
                  <UilBuilding size="16" />
                </template>
                <template #after-option-item="{ option, }">
                  <span class="etablissement-option-meta text-small">
                    {{ option.uai || $t('general.uai') }} · {{ option.ville || '-' }}
                  </span>
                </template>
              </SelectClassic>

              <p class="text-small etablissement-search-hint">
                {{ isHorsFranceProfil
                  ? $t('connexion.recherche-etablissement-hors-france')
                  : $t('connexion.recherche-etablissement-ministere')
                }}
              </p>

              <ButtonClassic
                variant="solid"
                color="secondary"
                class="etablissement-help-action"
                :label="$t('connexion.etablissement-introuvable-btn')"
                icon="right"
                @click="openEtablissementHelpModal"
              >
                <template #right-icon>
                  <UilArrowRight />
                </template>
              </ButtonClassic>

              <div
                v-if="selectedEtablissement"
                class="etablissement-selected"
              >
                <div class="etablissement-detail-row">
                  <span class="etablissement-detail-label">{{ $t('general.etablissement') }}</span>
                  <span class="etablissement-detail-value text-regular">
                    {{ selectedEtablissement.nom || selectedEtablissement.libelle || '-' }}
                  </span>
                </div>
                <div class="etablissement-detail-row">
                  <span class="etablissement-detail-label">Type / Niveau</span>
                  <span class="etablissement-detail-value text-regular">
                    {{ selectedEtablissement.type || '-' }} · {{ selectedEtablissement.niveau || '-' }}
                  </span>
                </div>
                <div class="etablissement-detail-row">
                  <span class="etablissement-detail-label">Adresse</span>
                  <span class="etablissement-detail-value text-regular">
                    {{ selectedEtablissement.adresse || '-' }}
                    <span v-if="selectedEtablissement.code_postal || selectedEtablissement.ville">
                      — {{ selectedEtablissement.code_postal || '' }} {{ selectedEtablissement.ville || '' }}
                    </span>
                  </span>
                </div>
                <div class="etablissement-detail-row">
                  <span class="etablissement-detail-label">{{ $t('general.uai') }}</span>
                  <span class="etablissement-detail-value text-regular">
                    {{ selectedEtablissement.uai || '-' }}
                  </span>
                </div>
              </div>
            </template>

            <template v-else-if="isExport">
              <p class="text-small etablissement-search-hint">
                {{ $t("connexion.etablissement-export-description") }}
              </p>

              <InputClassic
                :model-value="selectedEtablissement?.nom || ''"
                :label="$t('etablissement.nom-etablissement')"
                type="text"
                status="required"
                @update:model-value="updateManualEtablissementField('nom', $event)"
              />

              <div class="demande-form-row">
                <SelectClassic
                  :model-value="getOption(typeEtablissementOptions, selectedEtablissement?.type)"
                  :label="$t('connexion.champ-type-etablissement')"
                  :inline="false"
                  status="required"
                  :placeholder="$t('general.placeholder-selectionner')"
                  :options="typeEtablissementOptions"
                  @update:model-value="updateManualEtablissementField('type', $event?.value)"
                />

                <SelectClassic
                  :model-value="getOption(statutEtablissementOptions, selectedEtablissement?.statut_public_prive)"
                  :label="$t('connexion.champ-statut-etablissement')"
                  :inline="false"
                  status="required"
                  :placeholder="$t('general.placeholder-selectionner')"
                  :options="statutEtablissementOptions"
                  @update:model-value="updateManualEtablissementField('statut_public_prive', $event?.value)"
                />
              </div>

              <div class="demande-form-row">
                <InputClassic
                  :model-value="selectedEtablissement?.adresse || ''"
                  :label="$t('connexion.champ-adresse')"
                  type="text"
                  status="required"
                  @update:model-value="updateManualEtablissementField('adresse', $event)"
                />

                <InputClassic
                  :model-value="selectedEtablissement?.complement_adresse || ''"
                  :label="$t('connexion.champ-complement-adresse')"
                  type="text"
                  @update:model-value="updateManualEtablissementField('complement_adresse', $event)"
                />
              </div>

              <div class="demande-form-row">
                <InputClassic
                  :model-value="selectedEtablissement?.code_postal || ''"
                  :label="$t('connexion.champ-code-postal')"
                  type="text"
                  status="required"
                  @update:model-value="updateManualEtablissementField('code_postal', $event)"
                />

                <InputClassic
                  :model-value="selectedEtablissement?.ville || ''"
                  :label="$t('connexion.champ-ville')"
                  type="text"
                  status="required"
                  @update:model-value="updateManualEtablissementField('ville', $event)"
                />
              </div>

              <div class="demande-form-row">
                <InputClassic
                  :model-value="selectedEtablissement?.region || ''"
                  :label="$t(`connexion.champ-region-${paysCataloguePublic}`)"
                  type="text"
                  @update:model-value="updateManualEtablissementField('region', $event)"
                />

                <InputClassic
                  :model-value="paysLibelle"
                  :label="$t('connexion.champ-pays')"
                  type="text"
                  status="locked"
                  disabled
                />
              </div>

              <InputClassic
                :model-value="selectedEtablissement?.telephone || ''"
                :label="$t('connexion.champ-telephone')"
                type="tel"
                status="required"
                @update:model-value="updateManualEtablissementField('telephone', $event)"
              />
            </template>

            <template v-else>
              <p class="text-small etablissement-search-hint">
                {{ $t("connexion.etablissement-manuel-description") }}
              </p>

              <InputClassic
                :model-value="selectedEtablissement?.nom || ''"
                :label="$t('etablissement.nom-etablissement')"
                type="text"
                status="required"
                @update:model-value="updateManualEtablissementField('nom', $event)"
              />

              <InputClassic
                :model-value="selectedEtablissement?.type || ''"
                :label="$t('general.type')"
                type="text"
                status="required"
                @update:model-value="updateManualEtablissementField('type', $event)"
              />

              <InputClassic
                :model-value="selectedEtablissement?.niveau || ''"
                :label="$t('connexion.champ-niveau')"
                type="text"
                status="required"
                @update:model-value="updateManualEtablissementField('niveau', $event)"
              />

              <InputClassic
                :model-value="selectedEtablissement?.adresse || ''"
                :label="$t('connexion.champ-adresse')"
                type="text"
                status="required"
                @update:model-value="updateManualEtablissementField('adresse', $event)"
              />

              <InputClassic
                :model-value="selectedEtablissement?.code_postal || ''"
                :label="$t('connexion.champ-code-postal')"
                type="text"
                status="required"
                @update:model-value="updateManualEtablissementField('code_postal', $event)"
              />

              <InputClassic
                :model-value="selectedEtablissement?.ville || ''"
                :label="$t('connexion.champ-ville')"
                type="text"
                status="required"
                @update:model-value="updateManualEtablissementField('ville', $event)"
              />

              <InputClassic
                :model-value="selectedEtablissement?.pays || ''"
                :label="$t('connexion.champ-pays')"
                type="text"
                status="required"
                @update:model-value="updateManualEtablissementField('pays', $event)"
              />

              <InputClassic
                :model-value="selectedEtablissement?.uai || ''"
                :label="$t('connexion.champ-uai-optionnel')"
                type="text"
                @update:model-value="updateManualEtablissementField('uai', $event)"
              />
            </template>

            <div
              v-if="etablissementValidationIssues.length"
              class="civilite-validation-box"
            >
              <p class="text-small bold validation-title">
                {{ $t("connexion.validation-etablissement-title") }}
              </p>
              <ul class="text-small validation-list">
                <li
                  v-for="(issue, index) in etablissementValidationIssues"
                  :key="`etablissement-issue-${index}`"
                >
                  {{ issue }}
                </li>
              </ul>
            </div>
          </div>
        </template>
      </ModalTab>

      <!-- Tab 4 : Adresse de facturation -->
      <ModalTab v-show="activeTab === 4">
        <template #content>
          <div class="demande-form">
            <template v-if="!facturationDeferred">
              <p class="text-small bold facturation-section-title">
                {{ $t('connexion.champ-adresse') }}
              </p>

              <InputClassic
                :model-value="form.facturation_nom"
                :label="$t('connexion.facturation-champ-nom')"
                type="text"
                status="required"
                @update:model-value="form.facturation_nom = $event"
              />

              <InputClassic
                :model-value="form.facturation_adresse"
                :label="$t('connexion.facturation-champ-adresse')"
                type="text"
                status="required"
                @update:model-value="form.facturation_adresse = $event"
              />

              <InputClassic
                :model-value="form.facturation_code_postal"
                :label="$t('connexion.facturation-champ-code-postal')"
                type="text"
                status="required"
                @update:model-value="form.facturation_code_postal = $event"
              />

              <InputClassic
                :model-value="form.facturation_ville"
                :label="$t('connexion.facturation-champ-ville')"
                type="text"
                status="required"
                @update:model-value="form.facturation_ville = $event"
              />

              <InputClassic
                :model-value="form.facturation_pays"
                :label="$t('connexion.champ-pays')"
                type="text"
                status="required"
                @update:model-value="form.facturation_pays = $event"
              />

              <ButtonClassic
                v-if="selectedEtablissement"
                variant="solid"
                color="primary"
                size="medium"
                icon="left"
                class="facturation-autofill-btn"
                :label="$t('connexion.facturation-remplir-etablissement')"
                @click="fillFacturationFromEtablissement"
              >
                <template #left-icon>
                  <UilArrowUp size="16" />
                </template>
              </ButtonClassic>

              <div
                v-if="facturationAddressValidationIssues.length"
                class="civilite-validation-box"
              >
                <p class="text-small bold validation-title">
                  {{ $t('connexion.validation-facturation-title') }}
                </p>
                <ul class="text-small validation-list">
                  <li
                    v-for="(issue, index) in facturationAddressValidationIssues"
                    :key="`facturation-addr-issue-${index}`"
                  >
                    {{ issue }}
                  </li>
                </ul>
              </div>
            </template>
          </div>
        </template>
      </ModalTab>

      <!-- Tab 5 : Contact de facturation -->
      <ModalTab v-show="activeTab === 5">
        <template #content>
          <div class="demande-form">
            <template v-if="!facturationDeferred">
              <div class="flex-row facturation-use-requester-wrapper">
                <ButtonClassic
                  variant="solid"
                  color="primary"
                  size="medium"
                  icon="left"
                  class="facturation-use-requester-btn"
                  :label="$t('connexion.facturation-je-serai-contact')"
                  @click="copyRequesterToBillingContact"
                >
                  <template #left-icon>
                    <UilUser size="16" />
                  </template>
                </ButtonClassic>
              </div>

              <p class="text-small bold facturation-section-title">
                {{ $t('connexion.facturation-section-contact') }}
              </p>

              <SelectClassic
                v-model="selectedFacturationSalutationOption"
                :label="$t('connexion.facturation-champ-salutation')"
                :inline="false"
                :placeholder="$t('general.placeholder-selectionner')"
                :options="salutationOptions"
              />

              <SelectClassic
                v-model="selectedFacturationFonctionOption"
                :label="$t('connexion.champ-fonction')"
                :inline="false"
                :placeholder="$t('general.placeholder-selectionner')"
                :options="facturationFonctionOptions"
              />

              <InputClassic
                :model-value="form.facturation_contact_nom"
                :label="$t('connexion.facturation-champ-contact-nom')"
                type="text"
                status="required"
                @update:model-value="form.facturation_contact_nom = $event"
              />

              <InputClassic
                :model-value="form.facturation_contact_prenom"
                :label="$t('connexion.facturation-champ-contact-prenom')"
                type="text"
                status="required"
                @update:model-value="form.facturation_contact_prenom = $event"
              />

              <InputClassic
                :model-value="form.facturation_email"
                :label="$t('connexion.facturation-champ-courriel')"
                type="email"
                status="required"
                @update:model-value="form.facturation_email = $event"
              />

              <InputClassic
                v-if="!isExport"
                :model-value="form.facturation_siret"
                :label="$t('connexion.facturation-champ-siret')"
                type="text"
                status="required"
                maxlength="14"
                @update:model-value="form.facturation_siret = ($event || '').replace(/\s+/g, '').slice(0, 14)"
              />

              <label
                v-if="showChorusPaymentOption"
                class="legal-consent text-regular facturation-chorus-toggle"
              >
                <input
                  v-model="form.facturation_paiement_chorus"
                  type="checkbox"
                />
                <span>
                  {{ $t('connexion.facturation-paiement-chorus-label') }}
                </span>
              </label>

              <div
                v-if="facturationContactValidationIssues.length"
                class="civilite-validation-box"
              >
                <p class="text-small bold validation-title">
                  {{ $t('connexion.validation-facturation-title') }}
                </p>
                <ul class="text-small validation-list">
                  <li
                    v-for="(issue, index) in facturationContactValidationIssues"
                    :key="`facturation-contact-issue-${index}`"
                  >
                    {{ issue }}
                  </li>
                </ul>
              </div>
            </template>
          </div>
        </template>
      </ModalTab>

      <!-- Tab 6 : Confirmation -->
      <ModalTab v-show="activeTab === 6">
        <template #content>
          <div class="demande-form">
            <template v-if="demandeSoumise">
              <div class="readonly-field recap-field">
                <p class="text-medium bold">
                  {{ $t('connexion.demande-confirmation-modal-title') }}
                </p>
                <p class="text-regular">
                  {{ $t('connexion.demande-confirmation-modal-content') }}
                </p>
                <p
                  v-if="panierVideApresSoumission"
                  class="text-small"
                >
                  {{ $t('connexion.demande-panier-vide-confirmation') }}
                </p>
              </div>
            </template>

            <template v-else-if="hasDemandeDevisContext">
              <div class="confirmation-layout">
                <Toast
                  v-if="isCanada"
                  :title="$t('connexion.devis-devise-canada')"
                  variant="warning"
                  show-icon
                  hide-close-btn
                />
                <div class="confirmation-recap-tabs">
                  <TabsGroup
                    :tabs="confirmationRecapTabs"
                    size="medium"
                    @change="onConfirmationRecapTabChange($event)"
                  />
                </div>

                <div
                  v-if="confirmationRecapTab === 'infos'"
                  class="confirmation-recap-pane"
                >
                  <div class="readonly-field recap-field recap-field--demandeur">
                    <div class="recap-header">
                      <p class="s6 bold">
                        {{ $t('demande-devis.demandeur') }}
                      </p>
                      <ButtonClassic
                        variant="ghost"
                        color="secondary"
                        size="small"
                        :label="$t('connexion.modifier-mes-informations')"
                        @click="setTab(2)"
                      />
                    </div>

                    <div class="recap-row">
                      <span class="recap-label">{{ $t('connexion.champ-salutation') }}</span>
                      <p class="text-regular">
                        {{ form.salutation || '-' }}
                      </p>
                    </div>
                    <div class="recap-row">
                      <span class="recap-label">{{ $t('connexion.champ-prenom') }}</span>
                      <p class="text-regular">
                        {{ form.prenom || '-' }}
                      </p>
                    </div>
                    <div class="recap-row">
                      <span class="recap-label">{{ $t('connexion.champ-nom') }}</span>
                      <p class="text-regular">
                        {{ form.nom || '-' }}
                      </p>
                    </div>
                    <div class="recap-row">
                      <span class="recap-label">{{ $t('general.email') }}</span>
                      <p class="text-regular">
                        {{ form.email || '-' }}
                      </p>
                    </div>
                    <div class="recap-row">
                      <span class="recap-label">{{ $t('connexion.champ-fonction') }}</span>
                      <p class="text-regular">
                        {{ selectedProfil ? selectedProfil.label : (form.fonction || '-') }}
                      </p>
                    </div>
                    <div
                      v-if="selectedEtablissement"
                      class="recap-row"
                    >
                      <span class="recap-label">{{ $t('general.etablissement') }}</span>
                      <p class="text-regular">
                        {{ selectedEtablissement.nom || '-' }}
                      </p>
                    </div>
                    <div
                      v-if="selectedEtablissement"
                      class="recap-row"
                    >
                      <span class="recap-label">{{ $t('general.uai') }}</span>
                      <p class="text-regular">
                        {{ recapUai }}
                      </p>
                    </div>
                    <div
                      v-if="form.motif"
                      class="recap-row"
                    >
                      <span class="recap-label">{{ $t('connexion.champ-motif') }}</span>
                      <p class="text-regular">
                        {{ form.motif || '-' }}
                      </p>
                    </div>
                    <template v-if="!facturationDeferred">
                      <div class="recap-row recap-row--section-title">
                        <span class="recap-label bold">{{ $t('connexion.champ-adresse') }}</span>
                      </div>
                      <div class="recap-row">
                        <span class="recap-label">{{ $t('connexion.facturation-champ-nom') }}</span>
                        <p class="text-regular">
                          {{ form.facturation_nom || '-' }}
                        </p>
                      </div>
                      <div class="recap-row">
                        <span class="recap-label">{{ $t('connexion.facturation-champ-adresse') }}</span>
                        <p class="text-regular">
                          {{ form.facturation_adresse || '-' }}
                        </p>
                      </div>
                      <div class="recap-row">
                        <span class="recap-label">{{ $t('connexion.facturation-champ-code-postal') }}</span>
                        <p class="text-regular">
                          {{ form.facturation_code_postal || '-' }}
                        </p>
                      </div>
                      <div class="recap-row">
                        <span class="recap-label">{{ $t('connexion.facturation-champ-ville') }}</span>
                        <p class="text-regular">
                          {{ form.facturation_ville || '-' }}
                        </p>
                      </div>
                      <div class="recap-row">
                        <span class="recap-label">{{ $t('connexion.champ-pays') }}</span>
                        <p class="text-regular">
                          {{ form.facturation_pays || '-' }}
                        </p>
                      </div>
                      <div class="recap-row recap-row--section-title">
                        <span class="recap-label bold">{{ $t('connexion.facturation-section-contact') }}</span>
                      </div>
                      <div class="recap-row">
                        <span class="recap-label">{{ $t('connexion.facturation-champ-salutation') }}</span>
                        <p class="text-regular">
                          {{ form.facturation_contact_salutation || '-' }}
                        </p>
                      </div>
                      <div class="recap-row">
                        <span class="recap-label">{{ $t('connexion.champ-fonction') }}</span>
                        <p class="text-regular">
                          {{ form.facturation_contact_fonction || '-' }}
                        </p>
                      </div>
                      <div class="recap-row">
                        <span class="recap-label">{{ $t('connexion.facturation-champ-contact-prenom') }}</span>
                        <p class="text-regular">
                          {{ form.facturation_contact_prenom || '-' }}
                        </p>
                      </div>
                      <div class="recap-row">
                        <span class="recap-label">{{ $t('connexion.facturation-champ-contact-nom') }}</span>
                        <p class="text-regular">
                          {{ form.facturation_contact_nom || '-' }}
                        </p>
                      </div>
                      <div class="recap-row">
                        <span class="recap-label">
                          {{ $t('connexion.facturation-champ-courriel') }}
                        </span>
                        <p class="text-regular">
                          {{ form.facturation_email || '-' }}
                        </p>
                      </div>
                      <div
                        v-if="!isExport"
                        class="recap-row"
                      >
                        <span class="recap-label">{{ $t('connexion.facturation-champ-siret') }}</span>
                        <p class="text-regular">
                          {{ form.facturation_siret || '-' }}
                        </p>
                      </div>
                    </template>
                  </div>
                </div>

                <div
                  v-else
                  class="confirmation-recap-pane"
                >
                  <div class="readonly-field recap-panier recap-panier--table">
                    <div class="recap-panier-header">
                      <p class="s6 bold">
                        {{ $t('demande-devis.recap-panier') }}
                      </p>
                    </div>

                    <Toast
                      v-if="isHorsFranceProfil"
                      :title="$t('connexion.devis-prix-hors-france-titre')"
                      :content="$t('connexion.devis-prix-hors-france-hint')"
                      variant="warning"
                      show-icon
                      hide-close-btn
                      class="recap-prix-hors-france-hint"
                    />

                    <div class="recap-panier-meta text-regular">
                      <div class="recap-row">
                        <span class="recap-label">{{ $t('connexion.recap-panier-nombre-lignes') }}</span>
                        <p class="text-regular">
                          {{ demandeDevisLignes.length }}
                        </p>
                      </div>
                      <div class="recap-row">
                        <span class="recap-label">{{ $t('connexion.recap-panier-quantite-totale') }}</span>
                        <p class="text-regular">
                          {{ demandeDevisQuantiteTotale }}
                        </p>
                      </div>
                      <div class="recap-row">
                        <span class="recap-label">{{ $t('connexion.recap-panier-total-ttc') }}</span>
                        <p class="text-regular">
                          {{ formatRecapPrice(demandeCompteContext?.total_ttc) }}
                        </p>
                      </div>
                    </div>

                    <ProductTable
                      v-if="demandeDevisProductRows.length"
                      class="demande-devis-product-table"
                      :rows="demandeDevisProductRows"
                      :disable-editing="true"
                      :no-deletion-possible="true"
                      :no-sorting="true"
                    />
                    <p
                      v-else
                      class="text-regular"
                    >
                      {{ $t('panier.votre-panier-est-vide') }}
                    </p>
                  </div>
                </div>

                <div class="legal-consents">
                  <label class="legal-consent text-regular">
                    <input
                      v-model="viderPanierApresDemande"
                      type="checkbox"
                      :true-value="true"
                      :false-value="false"
                    />
                    <span>{{ $t('connexion.vider-panier-apres-demande-label') }}</span>
                  </label>
                </div>
              </div>
            </template>

            <div
              v-else-if="!demandeSoumise"
              class="readonly-field recap-field"
            >
              <div class="recap-row">
                <span class="recap-label">{{ $t('connexion.champ-salutation') }}</span>
                <p class="text-regular">
                  {{ form.salutation || '-' }}
                </p>
              </div>
              <div class="recap-row">
                <span class="recap-label">{{ $t('connexion.champ-prenom') }}</span>
                <p class="text-regular">
                  {{ form.prenom || '-' }}
                </p>
              </div>
              <div class="recap-row">
                <span class="recap-label">{{ $t('connexion.champ-nom') }}</span>
                <p class="text-regular">
                  {{ form.nom || '-' }}
                </p>
              </div>
              <div class="recap-row">
                <span class="recap-label">{{ $t('general.email') }}</span>
                <p class="text-regular">
                  {{ form.email || '-' }}
                </p>
              </div>
              <div class="recap-row">
                <span class="recap-label">{{ $t('connexion.champ-fonction') }}</span>
                <p class="text-regular">
                  {{ selectedProfil ? selectedProfil.label : (form.fonction || '-') }}
                </p>
              </div>
              <div class="recap-row">
                <span class="recap-label">{{ $t('connexion.champ-motif') }}</span>
                <p class="text-regular">
                  {{ form.motif || '-' }}
                </p>
              </div>
              <template v-if="!facturationDeferred">
                <div class="recap-row recap-row--section-title">
                  <span class="recap-label bold">{{ $t('connexion.champ-adresse') }}</span>
                </div>
                <div class="recap-row">
                  <span class="recap-label">{{ $t('connexion.facturation-champ-nom') }}</span>
                  <p class="text-regular">
                    {{ form.facturation_nom || '-' }}
                  </p>
                </div>
                <div class="recap-row">
                  <span class="recap-label">{{ $t('connexion.facturation-champ-adresse') }}</span>
                  <p class="text-regular">
                    {{ form.facturation_adresse || '-' }}
                  </p>
                </div>
                <div class="recap-row">
                  <span class="recap-label">{{ $t('connexion.facturation-champ-code-postal') }}</span>
                  <p class="text-regular">
                    {{ form.facturation_code_postal || '-' }}
                  </p>
                </div>
                <div class="recap-row">
                  <span class="recap-label">{{ $t('connexion.facturation-champ-ville') }}</span>
                  <p class="text-regular">
                    {{ form.facturation_ville || '-' }}
                  </p>
                </div>
                <div class="recap-row">
                  <span class="recap-label">{{ $t('connexion.champ-pays') }}</span>
                  <p class="text-regular">
                    {{ form.facturation_pays || '-' }}
                  </p>
                </div>
                <div class="recap-row recap-row--section-title">
                  <span class="recap-label bold">{{ $t('connexion.facturation-section-contact') }}</span>
                </div>
                <div class="recap-row">
                  <span class="recap-label">{{ $t('connexion.facturation-champ-salutation') }}</span>
                  <p class="text-regular">
                    {{ form.facturation_contact_salutation || '-' }}
                  </p>
                </div>
                <div class="recap-row">
                  <span class="recap-label">{{ $t('connexion.champ-fonction') }}</span>
                  <p class="text-regular">
                    {{ form.facturation_contact_fonction || '-' }}
                  </p>
                </div>
                <div class="recap-row">
                  <span class="recap-label">{{ $t('connexion.facturation-champ-contact-prenom') }}</span>
                  <p class="text-regular">
                    {{ form.facturation_contact_prenom || '-' }}
                  </p>
                </div>
                <div class="recap-row">
                  <span class="recap-label">{{ $t('connexion.facturation-champ-contact-nom') }}</span>
                  <p class="text-regular">
                    {{ form.facturation_contact_nom || '-' }}
                  </p>
                </div>
                <div class="recap-row">
                  <span class="recap-label">
                    {{ $t('connexion.facturation-champ-courriel') }}
                  </span>
                  <p class="text-regular">
                    {{ form.facturation_email || '-' }}
                  </p>
                </div>
                <div
                  v-if="!isExport"
                  class="recap-row"
                >
                  <span class="recap-label">{{ $t('connexion.facturation-champ-siret') }}</span>
                  <p class="text-regular">
                    {{ form.facturation_siret || '-' }}
                  </p>
                </div>
              </template>
              <div
                v-if="selectedEtablissement"
                class="recap-row"
              >
                <span class="recap-label">{{ $t('general.etablissement') }}</span>
                <p class="text-regular">
                  {{ selectedEtablissement.nom || '-' }}
                </p>
              </div>
              <div
                v-if="selectedEtablissement"
                class="recap-row"
              >
                <span class="recap-label">{{ $t('general.uai') }}</span>
                <p class="text-regular">
                  {{ recapUai }}
                </p>
              </div>
            </div>

            <div
              v-if="!demandeSoumise"
              class="legal-box"
            >
              <p class="text-small">
                {{ $t('connexion.informations-legales-texte') }}
              </p>
            </div>

            <div
              v-if="!demandeSoumise"
              class="legal-consents"
            >
              <label class="legal-consent text-regular">
                <input
                  v-model="form.legalConsent"
                  type="checkbox"
                />
                <span>{{ $t('connexion.consentement-donnees-label') }}</span>
              </label>

              <label class="legal-consent text-regular">
                <input
                  v-model="form.marketingConsent"
                  type="checkbox"
                />
                <span>{{ $t('connexion.consentement-offres-label') }}</span>
              </label>
            </div>
          </div>
        </template>
      </ModalTab>

      <!-- Tab 7 : Établissement hors France info contact -->
      <ModalTab v-show="activeTab === 7">
        <template #content>
          <p class="text-regular">
            {{ $t('connexion.hors-france-modal-content') }}
          </p>
        </template>
      </ModalTab>
    </template>

    <template #footer>
      <Toast
        v-if="activeTab === 2 && emailReconnu"
        :title="$t('connexion.email-reconnu-titre')"
        :content="$t('connexion.email-reconnu-message')"
        variant="error"
        show-icon
        hide-close-btn
        class="email-reconnu-toast"
      />
      <Toast
        v-if="activeTab === 3 && isEtablissementReconnu"
        :title="$t('connexion.etablissement-reconnu-titre')"
        variant="warning"
        show-icon
        hide-close-btn
        class="etablissement-reconnu-toast"
      >
        <template #content>
          <p
            class="content text-medium"
            v-html="$t('connexion.etablissement-reconnu-message')"
          />
          <!-- E-mail en interpolation (échappé), hors du v-html -->
          <p
            v-if="contactGestionnaireMasque"
            class="content text-medium contact-gestionnaire"
          >
            {{ $t("connexion.etablissement-reconnu-contact-gestionnaire") }}
            <strong>{{ contactGestionnaireMasque }}</strong>
          </p>
        </template>
      </Toast>
      <div
        v-if="activeTab > 0"
        class="modal-connexion-footer-actions"
      >
        <ButtonClassic
          v-if="activeTab === 1"
          variant="ghost"
          color="secondary"
          :label="$t('action.retour')"
          icon="left"
          @click="setTab(0)"
        >
          <template #left-icon>
            <UilArrowLeft />
          </template>
        </ButtonClassic>

        <template v-if="activeTab === 2">
          <ButtonClassic
            variant="ghost"
            color="secondary"
            :label="$t('action.retour')"
            icon="left"
            @click="setTab(1)"
          >
            <template #left-icon>
              <UilArrowLeft />
            </template>
          </ButtonClassic>

          <ButtonClassic
            v-if="emailReconnu"
            variant="solid"
            color="primary"
            :label="$t('connexion.email-reconnu-action')"
            icon="right"
            @click="goToLogin()"
          >
            <template #right-icon>
              <UilArrowRight />
            </template>
          </ButtonClassic>
          <ButtonClassic
            v-else
            variant="solid"
            color="primary"
            :label="$t('action.continuer')"
            :disabled="!isCiviliteValid || isLoading"
            @click="onCiviliteNext()"
          />
        </template>

        <template v-if="activeTab === 3 && needsEtablissement">
          <ButtonClassic
            variant="ghost"
            color="secondary"
            :label="$t('action.retour')"
            icon="left"
            @click="setTab(2)"
          >
            <template #left-icon>
              <UilArrowLeft />
            </template>
          </ButtonClassic>

          <ButtonClassic
            variant="solid"
            color="primary"
            :label="$t('action.continuer')"
            :disabled="!isEtablissementValid || isLoading"
            @click="onEtablissementNext()"
          />
        </template>

        <template v-if="activeTab === 4">
          <ButtonClassic
            variant="ghost"
            color="secondary"
            :label="$t('action.retour')"
            icon="left"
            @click="onFacturationBack()"
          >
            <template #left-icon>
              <UilArrowLeft />
            </template>
          </ButtonClassic>

          <div class="facturation-footer-right">
            <ButtonClassic
              variant="ghost"
              color="secondary"
              :label="$t('connexion.facturation-renseigner-ulterieurement')"
              @click="onFacturationDeferred()"
            />

            <ButtonClassic
              variant="solid"
              color="primary"
              :label="$t('action.continuer')"
              :disabled="!isFacturationAddressValid || isLoading"
              @click="onFacturationAddressNext()"
            />
          </div>
        </template>

        <template v-if="activeTab === 5">
          <ButtonClassic
            variant="ghost"
            color="secondary"
            :label="$t('action.retour')"
            icon="left"
            @click="onFacturationContactBack()"
          >
            <template #left-icon>
              <UilArrowLeft />
            </template>
          </ButtonClassic>

          <div class="facturation-footer-right">
            <ButtonClassic
              variant="ghost"
              color="secondary"
              :label="$t('connexion.facturation-renseigner-ulterieurement')"
              @click="onFacturationDeferred()"
            />

            <ButtonClassic
              variant="solid"
              color="primary"
              :label="$t('action.continuer')"
              :disabled="!isFacturationContactValid || isLoading"
              @click="onFacturationContactNext()"
            />
          </div>
        </template>

        <template v-if="activeTab === 6">
          <template v-if="demandeSoumise">
            <ButtonClassic
              variant="solid"
              color="primary"
              :label="$t('action.fermer')"
              @click="closeModalAfterDemande()"
            />
          </template>

          <template v-else>
            <ButtonClassic
              variant="ghost"
              color="secondary"
              :label="$t('action.retour')"
              icon="left"
              @click="onConfirmationBack()"
            >
              <template #left-icon>
                <UilArrowLeft />
              </template>
            </ButtonClassic>

            <InfiniteLoader v-if="isLoading" />
            <ButtonClassic
              v-else
              variant="solid"
              :color="hasDemandeDevisContext && isCommandeCtaProfil && !facturationDeferred ? 'secondary' : 'primary'"
              :label="submitLabel"
              :disabled="!isConfirmationValid"
              @click="submitDemande()"
            />
          </template>
        </template>

        <template v-if="activeTab === 7">
          <ButtonClassic
            variant="ghost"
            color="secondary"
            :label="$t('action.retour')"
            icon="left"
            @click="setTab(1)"
          >
            <template #left-icon>
              <UilArrowLeft />
            </template>
          </ButtonClassic>

          <ButtonClassic
            variant="solid"
            color="primary"
            :label="$t('connexion.contacter-par-courriel')"
            @click="openHorsFranceMailto()"
          />
        </template>
      </div>
    </template>
  </Modal>

  <ModalEtablissementIntrouvable
    :modal-id="etablissementHelpModalId"
    :contact-email="etablissementContactEmail"
  />
</template>

<script>
import {
  ButtonClassic,
  InfiniteLoader,
  InputClassic,
  Modal,
  SelectClassic,
  TabsGroup,
  Toast,
} from "@lde/core_lde_vue";
import {
  UilArrowRight,
  UilArrowLeft,
  UilBuilding,
  UilArrowUp,
  UilUser,
} from "@lde/vue-unicons";

import IconEnseignant from "@/components/icons/IconEnseignant.vue";
import IconDirectionIntendance from "@/components/icons/IconDirectionIntendance.vue";
import IconMairie from "@/components/icons/IconMairie.vue";
import IconPanierFleche from "@/components/icons/IconPanierFleche.vue";
import IconParticulier from "@/components/icons/IconParticulier.vue";
import IconProfDoc from "@/components/icons/IconProfDoc.vue";
import ModalEtablissementIntrouvable from "@/components/modals/ModalEtablissementIntrouvable.vue";
import ModalTab from "@/components/modals/ModalTab.vue";
import ProductTable from "@/components/table/ProductTable.vue";
import config from "@/config";
import mixinDevisPublicForm from "@/mixins/mixinDevisPublicForm";
import Api from "@/modules/axios";
import {
  PAYS_CANADA,
  PAYS_FRANCE,
  getTypeEtablissementOptions,
  isPaysExport,
} from "@/modules/cataloguePublic";
import { setHideModalImportPanier } from "@/modules/utils";

export default {
  name: "ModalConnexion",
  components: {
    ButtonClassic,
    InfiniteLoader,
    InputClassic,
    Modal,
    SelectClassic,
    TabsGroup,
    Toast,
    ModalEtablissementIntrouvable,
    ModalTab,
    ProductTable,
    UilArrowRight,
    UilArrowLeft,
    UilBuilding,
    UilArrowUp,
    UilUser,
    IconEnseignant,
    IconDirectionIntendance,
    IconMairie,
    IconPanierFleche,
    IconParticulier,
    IconProfDoc,
  },
  mixins: [mixinDevisPublicForm],
  data() {
    return {
      activeTab: 0,
      selectedProfil: null,
      isLoading: false,
      emailReconnu: false,
      form: {
        salutation: "",
        nom: "",
        prenom: "",
        email: "",
        motif: "",
        fonction: "",
        categorie: "",
        facturation_contact_salutation: "",
        facturation_contact_fonction: "",
        facturation_contact_nom: "",
        facturation_contact_prenom: "",
        facturation_nom: "",
        facturation_adresse: "",
        facturation_code_postal: "",
        facturation_ville: "",
        facturation_pays: "",
        facturation_email: "",
        facturation_siret: "",
        facturation_paiement_chorus: false,
        legalConsent: false,
        marketingConsent: false,
      },
      demandeSoumise: false,
      panierVideApresSoumission: false,
      viderPanierApresDemande: true,
      facturationDeferred: false,
      confirmationRecapTab: "infos",
      etablissementQuery: "",
      etablissementResults: [],
      selectedEtablissement: null,
      demandeCompteContext: null,
      isSearchingEtablissements: false,
      searchDebounceTimer: null,
      emailRecognitionDebounceTimer: null,
      emailRecognitionRequestId: 0,
      hasTriggeredEmailRecognitionRedirect: false,
      lastRecognizedEmail: "",
      etablissementRecognitionDebounceTimer: null,
      lastRecognizedEtablissementSignature: "",
      hasShownEtablissementRecognitionWarning: false,
      isEtablissementReconnu: false,
      /**
       * E-mail masqué du gestionnaire de l'établissement reconnu par son UAI (ex. th****@*****.***).
       */
      contactGestionnaireMasque: null,
      minEtablissementSearchLength: 3,
      profils: [
        {
          id: "enseignant",
          label: this.$t("connexion.profil-enseignant"),
          fonction: "Enseignant (autres)",
          categorie: "Pedagogique",
          icon: IconEnseignant,
          zone: "france",
          requiresEtablissement: true,
        },
        {
          id: "documentaliste",
          label: this.$t("connexion.profil-documentaliste"),
          fonction: "Professeur documentaliste",
          categorie: "CDI",
          icon: IconProfDoc,
          zone: "france",
          requiresEtablissement: true,
        },
        {
          id: "direction",
          label: this.$t("connexion.profil-direction"),
          fonction: "Chef d'établissement",
          categorie: "Direction",
          icon: IconDirectionIntendance,
          zone: "france",
          requiresEtablissement: true,
        },
        {
          id: "intendance",
          label: this.$t("connexion.profil-intendance"),
          fonction: "Gestionnaire",
          categorie: "Fin & Adm",
          icon: IconDirectionIntendance,
          zone: "france",
          requiresEtablissement: true,
        },
        {
          id: "mairie-administration",
          label: this.$t("connexion.profil-mairie-administration"),
          fonction: "Administratif (Mairie)",
          categorie: "Collectivite",
          icon: IconMairie,
          zone: "france",
          requiresEtablissement: true,
        },
        {
          id: "autre",
          label: this.$t("connexion.profil-autre"),
          fonction: "Autre",
          categorie: "Autres",
          icon: IconParticulier,
          zone: "france",
          requiresEtablissement: true,
        },
        {
          id: "hors-france",
          label: "Établissement hors France",
          fonction: "Autre",
          categorie: "Autres",
          icon: IconMairie,
          zone: "hors-france",
          requiresEtablissement: true,
        },
      ],
    };
  },
  /**
   * Computed properties
   */
  computed: {
    /**
     * Titre de la modale en fonction de l'onglet actif et du contexte.
     */
    modalTitle() {
      let baseTitle = this.$t("connexion.creer-un-compte");

      if (this.activeTab === 0) {
        baseTitle = this.hasDemandeDevisContext
          ? this.$t("connexion.creer-un-compte")
          : this.$t("action.se-connecter");
      }
      if (this.activeTab === 2) baseTitle = this.$t("connexion.etape-civilite-titre");
      if (this.activeTab === 3) baseTitle = this.$t("connexion.etape-etablissement-titre");
      if (this.activeTab === 4) baseTitle = this.$t("connexion.etape-facturation-adresse-titre");
      if (this.activeTab === 5) baseTitle = this.$t("connexion.etape-facturation-contact-titre");
      if (this.activeTab === 6) baseTitle = this.$t("connexion.confirmation-titre");
      if (this.activeTab === 7) baseTitle = this.$t("connexion.hors-france-modal-title");

      if (this.hasDemandeDevisContext) {
        return `${this.$t("action.demander-devis")} · ${baseTitle}`;
      }

      return baseTitle;
    },
    /**
     * Sous-titre de la modale en fonction de l'onglet actif.
     */
    modalSubtitle() {
      if (this.activeTab === 0) return this.tab0DetailsText;
      if (this.activeTab === 1) return this.tab1DetailsText;
      if (this.activeTab === 2) return this.tab2DetailsText;
      if (this.activeTab === 3) return this.tab3DetailsText;
      if (this.activeTab === 4) return this.tab4DetailsText;
      if (this.activeTab === 5) return this.tab5DetailsText;
      if (this.activeTab === 6) return this.tab6DetailsText;
      if (this.activeTab === 7) return this.$t("connexion.hors-france-modal-subtitle");

      return "";
    },
    /**
     * Classes CSS additionnelles pour la modale.
     */
    modalClass() {
      if (this.activeTab > 0) {
        return "wide modal-connexion modal-connexion--inscription";
      }

      return "wide modal-connexion";
    },
    /**
     * Liste des profils pour la France.
     */
    profilsFrance() {
      return this.profils.filter((profil) => profil.zone !== "hors-france");
    },
    /**
     * Liste des profils hors France.
     */
    profilsHorsFrance() {
      return this.profils.filter((profil) => profil.zone === "hors-france");
    },
    /**
     * Indique si le profil sélectionné est hors France.
     */
    isHorsFranceProfil() {
      return this.selectedProfil?.zone === "hors-france";
    },
    /**
     * Pays choisi par le visiteur à son arrivée sur le catalogue public.
     */
    paysCataloguePublic() {
      return this.$store.getters.cataloguePublicPays || PAYS_FRANCE;
    },
    /**
     * Parcours Export (Suisse, Belgique) : l'établissement est créé via un formulaire complet.
     */
    isExport() {
      return isPaysExport(this.paysCataloguePublic);
    },
    /**
     * Les devis sont établis en euros : le visiteur canadien est prévenu qu'ils ne seront pas en CAD.
     */
    isCanada() {
      return this.paysCataloguePublic === PAYS_CANADA;
    },
    paysLibelle() {
      return this.$t(`catalogue-public.pays-${this.paysCataloguePublic}`);
    },
    /**
     * Types d'établissement proposés dans le formulaire de création (parcours Export).
     */
    typeEtablissementOptions() {
      return getTypeEtablissementOptions(this.$t);
    },
    statutEtablissementOptions() {
      return [
        { id: "public", label: this.$t("etablissement.public"), value: "Public" },
        { id: "prive", label: this.$t("etablissement.prive"), value: "Privé" },
      ];
    },
    /**
     * UAI affiché dans le récapitulatif : à l'export, l'UAI fictif est attribué à l'envoi de la demande.
     */
    recapUai() {
      if (this.isExport) {
        return this.$t("connexion.uai-attribue-automatiquement");
      }

      return this.selectedEtablissement?.uai || "-";
    },
    /**
     * Adresse de courriel de contact par défaut.
     */
    contactEmail() {
      return config?.mails?.publicContact || "ressources.edu@lde.fr";
    },
    /**
     * Indique si l'onglet établissement est requis pour le profil.
     */
    needsEtablissement() {
      return Boolean(this.selectedProfil) && this.selectedProfil?.requiresEtablissement !== false;
    },
    /**
     * Indique si l'établissement est optionnel pour le profil.
     */
    isEtablissementOptionalProfile() {
      return this.selectedProfil?.id === "autre";
    },
    /**
     * Indique si la saisie de l'établissement doit se faire manuellement.
     */
    isManualEtablissement() {
      return this.needsEtablissement && (this.isHorsFranceProfil || this.isExport);
    },
    /**
     * Le paiement via Chorus Pro ne concerne que les établissements français.
     */
    showChorusPaymentOption() {
      return !this.isExport;
    },
    /**
     * Indique si le SIRET de facturation peut être pré-rempli.
     */
    isFacturationSiretPrefillProfile() {
      return this.hasDemandeDevisContext && this.needsEtablissement && !this.isManualEtablissement;
    },
    /**
     * ID de la modale d'aide pour l'établissement introuvable.
     */
    etablissementHelpModalId() {
      return "modal_etablissement_introuvable_connexion";
    },

    /**
     * Email de contact pour l'établissement introuvable.
     */
    etablissementContactEmail() {
      return config?.mails?.publicContact || "ressources.edu@lde.fr";
    },
    /**
     * Options pour le champ de salutation.
     */
    salutationOptions() {
      return [
        {
          id: "M",
          label: this.$t("connexion.salutation-m"),
          value: "M",
        },
        {
          id: "Mme",
          label: this.$t("connexion.salutation-mme"),
          value: "Mme",
        },
        {
          id: "Mx",
          label: this.$t("connexion.salutation-non-specifie"),
          value: "Mx",
        },
      ];
    },
    /**
     * Options de fonction pour la facturation.
     */
    facturationFonctionOptions() {
      return [
        "Administratif (Mairie)",
        "Agent comptable",
        "Chef d'établissement",
        "Directeur",
        "Gestionnaire",
        "Secrétaire",
        "Autre",
      ].map((value) => ({
        id: value,
        label: value,
        value,
      }));
    },
    /**
     * Getter/Setter pour l'option de salutation sélectionnée.
     */
    selectedSalutationOption: {
      get() {
        if (!this.form.salutation) {
          return null;
        }

        return this.salutationOptions.find((option) => option.value === this.form.salutation) || null;
      },
      set(option) {
        this.form.salutation = option?.value || "";
      },
    },
    /**
     * Getter/Setter pour la salutation de facturation sélectionnée.
     */
    selectedFacturationSalutationOption: {
      get() {
        if (!this.form.facturation_contact_salutation) {
          return null;
        }

        return this.salutationOptions.find(
          (option) => option.value === this.form.facturation_contact_salutation,
        ) || null;
      },
      set(option) {
        this.form.facturation_contact_salutation = option?.value || "";
      },
    },
    /**
     * Getter/Setter pour la fonction de facturation sélectionnée.
     */
    selectedFacturationFonctionOption: {
      get() {
        if (!this.form.facturation_contact_fonction) {
          return null;
        }

        return this.facturationFonctionOptions.find(
          (option) => option.value === this.form.facturation_contact_fonction,
        ) || null;
      },
      set(option) {
        this.form.facturation_contact_fonction = option?.value || "";
      },
    },
    /**
     * Options de résultats de recherche d'établissement formatées pour le select.
     */
    etablissementOptions() {
      return this.etablissementResults.map((etab, index) => ({
        id: etab.id || `${etab.uai || "etab"}-${index}`,
        label: etab.nom || etab.libelle || "-",
        value: etab.nom || etab.libelle || "-",
        uai: etab.uai || "",
        ville: etab.ville || "-",
        rawEtablissement: etab,
      }));
    },
    /**
     * Getter/Setter pour l'établissement sélectionné dans le select.
     */
    selectedEtablissementOption: {
      get() {
        if (!this.selectedEtablissement) {
          return null;
        }

        return {
          id: this.selectedEtablissement.id || this.selectedEtablissement.uai,
          label: this.selectedEtablissement.nom || this.selectedEtablissement.libelle,
          value: this.selectedEtablissement.nom || this.selectedEtablissement.libelle,
          uai: this.selectedEtablissement.uai || "",
          ville: this.selectedEtablissement.ville || "",
          rawEtablissement: this.selectedEtablissement,
        };
      },
      set(option) {
        if (!option) {
          this.selectedEtablissement = null;
          return;
        }

        this.selectEtablissement(option.rawEtablissement || option);
      },
    },
    /**
     * Indique si les informations de civilité sont valides.
     */
    isCiviliteValid() {
      if (!this.selectedProfil) return false;
      return this.civiliteValidationIssues.length === 0;
    },
    /**
     * Liste des problèmes de validation pour l'établissement.
     */
    etablissementValidationIssues() {
      if (!this.needsEtablissement) {
        return [];
      }

      const missingRequiredMessage = this.$t(
        "connexion.validation-etablissement-missing-required",
      );

      if (!this.selectedEtablissement) {
        if (this.isEtablissementOptionalProfile) {
          return [];
        }
        return [missingRequiredMessage];
      }

      if (!this.isManualEtablissement) {
        return [];
      }

      // Établissement facultatif : le formulaire de création peut rester vide
      if (this.isEtablissementOptionalProfile && this.isManualEtablissementEmpty) {
        return [];
      }

      const requiredKeys = this.isExport
        ? ["nom", "type", "statut_public_prive", "adresse", "code_postal", "ville", "telephone"]
        : ["nom", "type", "niveau", "adresse", "code_postal", "ville", "pays"];

      const hasMissingRequiredField = requiredKeys.some((key) => (
        this.isFieldEmpty(this.selectedEtablissement?.[key])
      ));

      return hasMissingRequiredField ? [missingRequiredMessage] : [];
    },
    /**
     * Indique si aucun champ du formulaire de création d'établissement n'a été renseigné.
     */
    isManualEtablissementEmpty() {
      return Object.entries(this.selectedEtablissement || {})
        .filter(([key]) => !["id", "pays"].includes(key))
        .every(([, value]) => this.isFieldEmpty(value));
    },
    /**
     * Indique si les informations d'établissement sont valides.
     */
    isEtablissementValid() {
      return this.etablissementValidationIssues.length === 0;
    },
    /**
     * Indique si les informations de facturation sont valides.
     */
    isFacturationValid() {
      return this.isFacturationAddressValid && this.isFacturationContactValid;
    },
    /**
     * Onglets pour le récapitulatif de confirmation.
     */
    confirmationRecapTabs() {
      const routerLinkObj = this.$route?.name
        ? {
          name: this.$route.name,
          params: { ...this.$route.params },
          query: { ...this.$route.query },
        }
        : {
          path: this.$route.path,
          query: { ...this.$route.query },
        };

      return [
        {
          key: "infos",
          title: this.$t("connexion.recap-infos-tab"),
          active: this.confirmationRecapTab === "infos",
          routerLinkObj,
        },
        {
          key: "panier",
          title: this.$t("connexion.recap-panier-tab"),
          active: this.confirmationRecapTab === "panier",
          routerLinkObj,
        },
      ];
    },
    /**
     * Indique si l'ensemble de la demande est valide pour soumission.
     */
    isConfirmationValid() {
      return this.isCiviliteValid
        && this.isEtablissementValid
        && (this.facturationDeferred || this.isFacturationValid)
        && Boolean(this.form.legalConsent);
    },
    /**
     * Indique si la demande se fait dans un contexte de demande de devis.
     */
    hasDemandeDevisContext() {
      if (!this.demandeCompteContext) return false;
      return Boolean(
        this.demandeCompteContext?.source === "panier"
        || this.demandeCompteContext?.destination === "devis"
        || this.demandeCompteContext?.panier_id,
      );
    },
    /**
     * Texte explicatif pour l'onglet 0.
     */
    tab0DetailsText() {
      if (!this.hasDemandeDevisContext) return this.$t("connexion.comment-acceder");
      return this.$t("connexion.devis-flow-tab0-details");
    },
    /**
     * Texte explicatif pour l'onglet 1.
     */
    tab1DetailsText() {
      if (!this.hasDemandeDevisContext) return this.$t("connexion.quel-profil-demande");
      return this.$t("connexion.devis-flow-tab1-details");
    },
    /**
     * Texte explicatif pour l'onglet 2.
     */
    tab2DetailsText() {
      if (!this.hasDemandeDevisContext) return this.$t("connexion.etape-civilite-description");
      return this.$t("connexion.devis-flow-tab2-details");
    },
    /**
     * Texte explicatif pour l'onglet 3.
     */
    tab3DetailsText() {
      if (this.isExport) return this.$t("connexion.etape-etablissement-export-description");
      return this.$t("connexion.etape-etablissement-description");
    },
    /**
     * Texte explicatif pour l'onglet 4.
     */
    tab4DetailsText() {
      if (!this.hasDemandeDevisContext) return this.$t("connexion.etape-facturation-description");
      return this.$t("connexion.devis-flow-tab4-details");
    },
    /**
     * Texte explicatif pour l'onglet 5.
     */
    tab5DetailsText() {
      return this.$t("connexion.devis-flow-tab5-details");
    },
    tab6DetailsText() {
      if (!this.hasDemandeDevisContext) return this.$t("connexion.confirmation-description");
      return this.$t("connexion.devis-flow-tab6-details");
    },
    /**
     * Indique si le profil est éligible au CTA de commande.
     */
    isCommandeCtaProfil() {
      return this.isCommandeActionProfileFromSignals(
        this.form?.fonction,
        this.form?.categorie,
        this.selectedProfil?.id,
      );
    },
    /**
     * Label du bouton de soumission.
     */
    submitLabel() {
      if (this.hasDemandeDevisContext) {
        return (this.isCommandeCtaProfil && !this.facturationDeferred)
          ? this.$t("connexion.devis-flow-submit-commande-label")
          : this.$t("connexion.devis-flow-submit-demande-label");
      }

      return this.$t("action.envoyer");
    },
    /**
     * Liste des problèmes de validation pour la civilité.
     */
    civiliteValidationIssues() {
      if (!this.selectedProfil) {
        return [this.$t("connexion.validation-civilite-profile-required")];
      }

      const requiredKeys = [
        "salutation",
        "nom",
        "prenom",
        "email",
        "fonction",
        "categorie",
      ];

      if (!this.hasDemandeDevisContext) {
        requiredKeys.push("motif");
      }

      const hasMissingRequiredField = requiredKeys.some((key) => this.isFieldEmpty(this.form[key]));

      const issues = [];

      if (hasMissingRequiredField) {
        issues.push(this.$t("connexion.validation-civilite-missing-required"));
      }

      if (!this.isFieldEmpty(this.form.email) && !this.isEmailValid(this.form.email)) {
        issues.push(this.$t("connexion.validation-civilite-invalid-email"));
      }

      return issues;
    },
    facturationValidationIssues() {
      if (this.facturationDeferred) {
        return [];
      }

      const requiredKeys = [
        "facturation_nom",
        "facturation_adresse",
        "facturation_code_postal",
        "facturation_ville",
        "facturation_contact_nom",
        "facturation_contact_prenom",
        "facturation_email",
      ];

      if (!this.isExport) {
        requiredKeys.push("facturation_siret");
      }

      const issues = [];
      const hasMissingRequiredField = requiredKeys.some((key) => this.isFieldEmpty(this.form[key]));

      if (hasMissingRequiredField) {
        issues.push(this.$t("connexion.validation-facturation-missing-required"));
      }

      if (!this.isFieldEmpty(this.form.facturation_email) && !this.isEmailValid(this.form.facturation_email)) {
        issues.push(this.$t("connexion.validation-facturation-invalid-email"));
      }

      return issues;
    },
    facturationAddressValidationIssues() {
      if (this.facturationDeferred) {
        return [];
      }

      const requiredKeys = [
        "facturation_nom",
        "facturation_adresse",
        "facturation_code_postal",
        "facturation_ville",
        "facturation_pays",
      ];

      const issues = [];
      const hasMissingRequiredField = requiredKeys.some((key) => this.isFieldEmpty(this.form[key]));

      if (hasMissingRequiredField) {
        issues.push(this.$t("connexion.validation-facturation-missing-required"));
      }

      return issues;
    },
    facturationContactValidationIssues() {
      if (this.facturationDeferred) {
        return [];
      }

      const requiredKeys = [
        "facturation_contact_nom",
        "facturation_contact_prenom",
        "facturation_email",
      ];

      if (!this.isExport) {
        requiredKeys.push("facturation_siret");
      }

      const issues = [];
      const hasMissingRequiredField = requiredKeys.some((key) => this.isFieldEmpty(this.form[key]));

      if (hasMissingRequiredField) {
        issues.push(this.$t("connexion.validation-facturation-missing-required"));
      }

      if (!this.isFieldEmpty(this.form.facturation_email) && !this.isEmailValid(this.form.facturation_email)) {
        issues.push(this.$t("connexion.validation-facturation-invalid-email"));
      }

      return issues;
    },
    isFacturationAddressValid() {
      return this.facturationAddressValidationIssues.length === 0;
    },
    isFacturationContactValid() {
      return this.facturationContactValidationIssues.length === 0;
    },
    /**
     * Lignes de demande de devis extraites du contexte.
     */
    demandeDevisLignes() {
      if (!Array.isArray(this.demandeCompteContext?.lignes)) {
        return [];
      }

      return this.demandeCompteContext.lignes;
    },
    /**
     * Lignes de produits formatées pour le tableau récapitulatif.
     */
    demandeDevisProductRows() {
      return this.demandeDevisLignes
        .map((ligne, index) => this.buildDemandeDevisProductRow(ligne, index))
        .filter(Boolean);
    },
    /**
     * Quantité totale des produits dans la demande de devis.
     */
    demandeDevisQuantiteTotale() {
      return this.demandeDevisLignes.reduce((total, ligne) => {
        const quantite = Number(ligne?.quantite || 0);
        if (Number.isNaN(quantite)) {
          return total;
        }

        return total + quantite;
      }, 0);
    },
  },
  watch: {
    selectedEtablissement(newVal) {
      if (!newVal) {
        this.isEtablissementReconnu = false;
      } else if (this.isManualEtablissement) {
        // Saisie manuelle : on évite un appel à chaque caractère
        this.scheduleEtablissementRecognitionCheck();
      } else {
        this.checkEtablissementRecognition();
      }
    },
  },
  /**
   * Lifecycle hooks
   */
  mounted() {
    window.addEventListener("modal-connexion:open", this.handleGlobalModalOpen);
  },
  beforeUnmount() {
    window.removeEventListener("modal-connexion:open", this.handleGlobalModalOpen);
  },
  /**
   * Component methods
   */
  methods: {
    /**
     * Gère l'événement global d'ouverture de la modale.
     */
    handleGlobalModalOpen(event) {
      // On stocke le contexte temporairement et on force l'ouverture de la modale
      this._pendingContext = event?.detail || null;
      this.$modal.show("modal_connexion");
    },
    /**
     * Ouvre la modale d'aide pour l'établissement introuvable.
     */
    openEtablissementHelpModal() {
      this.$modal.show(this.etablissementHelpModalId);
    },
    /**
     * Affiche une erreur de validation via un toast.
     */
    showValidationError(message) {
      this.$toast.error({ title: message });
    },
    /**
     * Gère la saisie de l'email.
     */
    onEmailInput(value) {
      this.form.email = value;
      this.emailReconnu = false;
    },
    /**
     * Vérifie si l'email est déjà reconnu par le système.
     */
    async checkEmailRecognition(email) {
      const normalizedEmail = this.normalizeRecognitionValue(email).toLowerCase();
      if (!this.isEmailValid(normalizedEmail)) {
        return false;
      }

      const requestId = this.emailRecognitionRequestId + 1;
      this.emailRecognitionRequestId = requestId;

      try {
        const response = await Api().post("/devis_public/check_recognition/", {
          email: normalizedEmail,
        });

        if (requestId !== this.emailRecognitionRequestId) {
          return false;
        }

        if (!response?.data?.email_reconnu) {
          return false;
        }

        if (this.hasTriggeredEmailRecognitionRedirect && this.lastRecognizedEmail === normalizedEmail) {
          return true;
        }

        this.lastRecognizedEmail = normalizedEmail;
        this.hasTriggeredEmailRecognitionRedirect = true;

        this.emailReconnu = true;

        return true;
      } catch {
        // Pas de blocage UX si la vérification de reconnaissance échoue.
        return false;
      }
    },
    /**
     * Construit le payload pour la reconnaissance d'établissement.
     */
    buildEtablissementRecognitionPayload() {
      if (!this.needsEtablissement || !this.selectedEtablissement) {
        return null;
      }

      const payload = {
        nom: this.normalizeRecognitionValue(
          this.selectedEtablissement.nom || this.selectedEtablissement.libelle,
        ),
        uai: this.normalizeRecognitionValue(this.selectedEtablissement.uai),
        adresse: this.normalizeRecognitionValue(this.selectedEtablissement.adresse),
        code_postal: this.normalizeRecognitionValue(this.selectedEtablissement.code_postal),
        ville: this.normalizeRecognitionValue(this.selectedEtablissement.ville),
      };

      const hasUai = payload.uai.length > 0;
      const availableAddressSignals = [
        payload.nom,
        payload.adresse,
        payload.code_postal,
        payload.ville,
      ].filter((value) => value.length > 0).length;

      if (!hasUai && availableAddressSignals < 2) {
        return null;
      }

      return payload;
    },
    /**
     * Génère une signature unique pour un établissement afin d'éviter les vérifications redondantes.
     */
    getEtablissementRecognitionSignature(etablissementPayload) {
      if (!etablissementPayload) {
        return "";
      }

      return [
        etablissementPayload.nom,
        etablissementPayload.uai,
        etablissementPayload.adresse,
        etablissementPayload.code_postal,
        etablissementPayload.ville,
      ]
        .map((value) => value.toLowerCase())
        .join("|");
    },
    /**
     * Planifie une vérification de reconnaissance d'établissement avec debounce.
     */
    scheduleEtablissementRecognitionCheck() {
      if (this.etablissementRecognitionDebounceTimer) {
        clearTimeout(this.etablissementRecognitionDebounceTimer);
        this.etablissementRecognitionDebounceTimer = null;
      }

      this.etablissementRecognitionDebounceTimer = setTimeout(() => {
        this.checkEtablissementRecognition();
      }, 450);
    },
    /**
     * Vérifie si l'établissement est déjà reconnu par le système.
     */
    async checkEtablissementRecognition() {
      const etablissementPayload = this.buildEtablissementRecognitionPayload();
      if (!etablissementPayload) {
        this.isEtablissementReconnu = false;
        return false;
      }

      const signature = this.getEtablissementRecognitionSignature(etablissementPayload);
      if (signature && this.lastRecognizedEtablissementSignature === signature) {
        return this.isEtablissementReconnu;
      }

      try {
        const response = await Api().post("/devis_public/check_recognition/", {
          etablissement: etablissementPayload,
        });

        if (!response?.data?.etablissement_reconnu) {
          this.isEtablissementReconnu = false;
          return false;
        }

        this.lastRecognizedEtablissementSignature = signature;
        this.isEtablissementReconnu = true;
        this.contactGestionnaireMasque = response.data.contact_gestionnaire || null;
        this.hasShownEtablissementRecognitionWarning = true;

        return true;
      } catch {
        // Pas de blocage UX si la vérification de reconnaissance échoue.
        this.isEtablissementReconnu = false;
        return false;
      }
    },
    /**
     * Formate un prix pour l'affichage récapitulatif.
     */
    formatRecapPrice(value) {
      if ([undefined, null, ""].includes(value)) return "-";
      const number = Number(value);
      if (Number.isNaN(number)) return value;

      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(number);
    },
    /**
     * Convertit une valeur en nombre ou null.
     */
    toNumberOrNull(value) {
      if ([undefined, null, ""].includes(value)) {
        return null;
      }

      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    },
    /**
     * Construit une offre récapitulative pour le tableau de produits.
     */
    buildRecapOfferForProductTable(ligne) {
      const catalogueType = ligne?.catalogue_type
        || (ligne?.reference_interdacta ? "fourniture" : "numerique");
      const offreId = ligne?.offre_id || ligne?.id_offre || null;
      const libelle = ligne?.libelle || this.$t("connexion.recap-panier-produit");
      const eanLde = ligne?.ean_lde || null;
      const urlCouverture = ligne?.url_couverture || null;
      const referenceInterdacta = ligne?.reference_interdacta || null;

      const baseOffer = {
        id: offreId,
        libelle,
        ean_lde: eanLde,
        prix_editeur: this.toNumberOrNull(ligne?.prix_unitaire_ttc),
        prix_ht: this.toNumberOrNull(ligne?.prix_unitaire_ht),
        quantite_min: 1,
        code_disponibilite: 1,
        adoptant: false,
      };

      const baseProduct = {
        id: offreId,
        libelle,
        ean: eanLde,
        ean_lde: eanLde,
        url_couverture: urlCouverture,
        reference_interdacta: referenceInterdacta,
      };

      const offerFromLine = (ligne?.offre && typeof ligne.offre === "object") ? { ...ligne.offre } : {};
      const mergedOffer = {
        ...baseOffer,
        ...offerFromLine,
      };

      let productKey = "manuel_numerique";
      let productOffreDefaut = { manuel_numerique: true };
      if (catalogueType === "papier") {
        productKey = "article";
        productOffreDefaut = { article: true };
      } else if (catalogueType === "fourniture") {
        productKey = "fourniture";
        productOffreDefaut = { fourniture: true };
      }

      const existingProduct = (mergedOffer?.[productKey] && typeof mergedOffer[productKey] === "object")
        ? mergedOffer[productKey]
        : {};

      mergedOffer[productKey] = {
        ...baseProduct,
        ...existingProduct,
        offre_defaut: {
          ...productOffreDefaut,
          ...((existingProduct.offre_defaut && typeof existingProduct.offre_defaut === "object")
            ? existingProduct.offre_defaut
            : {}),
        },
      };

      return mergedOffer;
    },
    /**
     * Construit une ligne de produit pour le tableau récapitulatif de demande de devis.
     */
    buildDemandeDevisProductRow(ligne, index) {
      if (!ligne || typeof ligne !== "object") {
        return null;
      }

      const quantite = Math.max(0, Number(ligne.quantite || 0));
      const prixUnitaireTtc = this.toNumberOrNull(ligne.prix_unitaire_ttc);
      const prixUnitaireHt = this.toNumberOrNull(ligne.prix_unitaire_ht);

      const totalLigneTtc = this.toNumberOrNull(ligne.total_ligne_ttc)
        ?? (prixUnitaireTtc !== null ? prixUnitaireTtc * quantite : null);
      const totalLigneHt = this.toNumberOrNull(ligne.total_ligne_ht)
        ?? (prixUnitaireHt !== null ? prixUnitaireHt * quantite : null);

      return {
        id: ligne.id_ligne || `${ligne.offre_id || "offre"}-${index}`,
        liste: true,
        quantite,
        offre: this.buildRecapOfferForProductTable(ligne),
        prix_unitaire_ttc: prixUnitaireTtc,
        prix_unitaire_ht: prixUnitaireHt,
        total_ligne_ttc: totalLigneTtc,
        total_ligne_ht: totalLigneHt,
        remise: 0,
      };
    },
    /**
     * Vérifie la validité d'un onglet avant de passer au suivant.
     */
    checkTabValid(tabIndex) {
      if (tabIndex === 2) {
        if (this.civiliteValidationIssues.length) {
          this.showValidationError(this.civiliteValidationIssues[0]);
          return false;
        }
        if (this.emailReconnu) {
          return false;
        }
      }

      if (tabIndex === 3) {
        if (this.etablissementValidationIssues.length) {
          this.showValidationError(this.etablissementValidationIssues[0]);
          return false;
        }
      }

      if (tabIndex === 4 && !this.facturationDeferred && this.facturationAddressValidationIssues.length) {
        this.showValidationError(this.facturationAddressValidationIssues[0]);
        return false;
      }

      if (tabIndex === 5 && !this.facturationDeferred && this.facturationContactValidationIssues.length) {
        this.showValidationError(this.facturationContactValidationIssues[0]);
        return false;
      }

      if (tabIndex === 6 && !this.form.legalConsent) {
        this.showValidationError(this.$t("connexion.consentement-donnees-manquant"));
        return false;
      }

      return true;
    },
    /**
     * Gère le changement d'onglet dans le récapitulatif de confirmation.
     */
    onConfirmationRecapTabChange(tab) {
      this.setConfirmationRecapTab(tab?.key || tab);
    },
    /**
     * Définit l'onglet actif dans le récapitulatif de confirmation.
     */
    setConfirmationRecapTab(tab) {
      if (!["infos", "panier"].includes(tab)) {
        return;
      }

      this.confirmationRecapTab = tab;
    },
    /**
     * Réinitialise l'état de la modale.
     */
    reset(event) {
      this.activeTab = 0;
      this.selectedProfil = null;
      this.demandeSoumise = false;
      this.panierVideApresSoumission = false;
      this.confirmationRecapTab = "infos";
      this.form = {
        salutation: "",
        nom: "",
        prenom: "",
        email: "",
        motif: "",
        fonction: "",
        categorie: "",
        facturation_contact_salutation: "",
        facturation_contact_fonction: "",
        facturation_contact_nom: "",
        facturation_contact_prenom: "",
        facturation_nom: "",
        facturation_adresse: "",
        facturation_code_postal: "",
        facturation_ville: "",
        facturation_pays: "",
        facturation_email: "",
        facturation_siret: "",
        facturation_paiement_chorus: false,
        legalConsent: false,
        marketingConsent: false,
      };
      this.etablissementQuery = "";
      this.etablissementResults = [];
      this.selectedEtablissement = null;

      // Récupération du contexte (priorité à vue-js-modal, sinon événement global de contournement)
      this.demandeCompteContext = (
        event?.params?.demandeCompteContext
        || this._pendingContext?.demandeCompteContext
        || null
      );

      // Ouverture directe sur une étape (ex. demande de compte choisie à l'arrivée sur le catalogue public)
      const ongletInitial = this._pendingContext?.ongletInitial;
      if (Number.isInteger(ongletInitial)) {
        this.activeTab = ongletInitial;
      }

      // On nettoie le contexte temporaire pour les futures ouvertures
      this._pendingContext = null;

      this.viderPanierApresDemande = Boolean(this.demandeCompteContext);
      this.facturationDeferred = false;
      this.isSearchingEtablissements = false;
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
      if (this.emailRecognitionDebounceTimer) {
        clearTimeout(this.emailRecognitionDebounceTimer);
        this.emailRecognitionDebounceTimer = null;
      }
      if (this.etablissementRecognitionDebounceTimer) {
        clearTimeout(this.etablissementRecognitionDebounceTimer);
        this.etablissementRecognitionDebounceTimer = null;
      }
      this.emailRecognitionRequestId = 0;
      this.hasTriggeredEmailRecognitionRedirect = false;
      this.emailReconnu = false;
      this.lastRecognizedEmail = "";
      this.lastRecognizedEtablissementSignature = "";
      this.contactGestionnaireMasque = null;
      this.hasShownEtablissementRecognitionWarning = false;
      this.isEtablissementReconnu = false;
      this.isLoading = false;
    },
    /**
     * Définit l'onglet actif de la modale.
     */
    setTab(index) {
      this.activeTab = index;
    },
    /**
     * Redirige l'utilisateur vers la page de connexion.
     */
    goToLogin() {
      window.location.href = config.loginUrl;
    },
    /**
     * Sélectionne un profil et initialise le formulaire correspondant.
     */
    selectProfil(profil) {
      this.selectedProfil = profil;
      this.form = {
        salutation: "",
        nom: "",
        prenom: "",
        email: "",
        motif: "",
        fonction: profil.fonction,
        categorie: profil.categorie,
        facturation_contact_salutation: "",
        facturation_contact_fonction: "",
        facturation_contact_nom: "",
        facturation_contact_prenom: "",
        facturation_nom: "",
        facturation_adresse: "",
        facturation_code_postal: "",
        facturation_ville: "",
        facturation_pays: this.getFacturationPaysParDefaut(profil),
        facturation_email: "",
        facturation_siret: "",
        facturation_paiement_chorus: false,
        legalConsent: false,
        marketingConsent: false,
      };
      this.facturationDeferred = false;
      this.etablissementQuery = "";
      this.etablissementResults = [];
      this.selectedEtablissement = this.isManualEtablissement
        ? this.createEmptyManualEtablissement()
        : null;

      if (this.emailRecognitionDebounceTimer) {
        clearTimeout(this.emailRecognitionDebounceTimer);
        this.emailRecognitionDebounceTimer = null;
      }
      if (this.etablissementRecognitionDebounceTimer) {
        clearTimeout(this.etablissementRecognitionDebounceTimer);
        this.etablissementRecognitionDebounceTimer = null;
      }
      this.emailRecognitionRequestId = 0;
      this.hasTriggeredEmailRecognitionRedirect = false;
      this.emailReconnu = false;
      this.lastRecognizedEmail = "";
      this.lastRecognizedEtablissementSignature = "";
      this.contactGestionnaireMasque = null;
      this.hasShownEtablissementRecognitionWarning = false;
      this.isEtablissementReconnu = false;

      if (profil.zone === "hors-france") {
        this.setTab(7);
        return;
      }

      this.setTab(2);
    },
    /**
     * Gère le passage à l'étape suivante après la saisie de la civilité.
     */
    async onCiviliteNext() {
      if (!this.checkTabValid(2)) return;

      const normalizedEmail = this.normalizeRecognitionValue(this.form.email).toLowerCase();
      if (this.isEmailValid(normalizedEmail)) {
        this.isLoading = true;
        try {
          const emailReconnu = await this.checkEmailRecognition(normalizedEmail);
          if (emailReconnu) return;
        } finally {
          this.isLoading = false;
        }
      }

      if (this.needsEtablissement) {
        this.setTab(3);
        return;
      }

      this.confirmationRecapTab = "infos";
      this.setTab(4);
    },
    /**
     * Gère le passage à l'étape suivante après la sélection de l'établissement.
     */
    async onEtablissementNext() {
      if (!this.checkTabValid(3)) return;

      this.isLoading = true;
      let etablissementReconnu = false;
      try {
        etablissementReconnu = await this.checkEtablissementRecognition();
      } finally {
        this.isLoading = false;
      }
      this.confirmationRecapTab = "infos";
      // Établissement déjà client : le CRM a sa facturation, on passe directement à la confirmation
      this.facturationDeferred = etablissementReconnu;
      this.setTab(etablissementReconnu ? 6 : 4);
    },
    /**
     * Revient à l'étape précédente depuis la facturation (Adresse).
     */
    onFacturationBack() {
      if (this.needsEtablissement) {
        this.setTab(3);
        return;
      }

      this.setTab(2);
    },
    /**
     * Revient à l'étape précédente depuis le contact de facturation.
     */
    onFacturationContactBack() {
      this.setTab(4);
    },
    /**
     * Reporte la saisie des informations de facturation.
     */
    onFacturationDeferred() {
      this.facturationDeferred = true;
      this.confirmationRecapTab = "infos";
      this.setTab(6);
    },
    /**
     * Gère le passage à l'étape suivante après l'adresse de facturation.
     */
    onFacturationAddressNext() {
      if (!this.checkTabValid(4)) return;
      this.setTab(5);
    },
    /**
     * Gère le passage à l'étape suivante après le contact de facturation.
     */
    onFacturationContactNext() {
      if (!this.checkTabValid(5)) return;
      this.confirmationRecapTab = "infos";
      this.setTab(6);
    },
    /**
     * Revient à l'étape précédente depuis la confirmation.
     */
    onConfirmationBack() {
      const wasDeferred = this.facturationDeferred;
      this.facturationDeferred = false;
      if (this.isEtablissementReconnu) {
        // Facturation sautée pour un établissement déjà client : retour à l'établissement
        this.setTab(3);
        return;
      }
      this.setTab(wasDeferred ? 4 : 5);
    },
    /**
     * Crée un objet établissement vide pour la saisie manuelle.
     */
    createEmptyManualEtablissement() {
      const etablissement = {
        id: null,
        nom: "",
        niveau: "",
        type: "",
        adresse: "",
        code_postal: "",
        ville: "",
        pays: "",
        uai: "",
        siren_siret: "",
        statut_public_prive: "",
      };

      if (!this.isExport) {
        return etablissement;
      }

      // Champs du formulaire de création d'établissement (parcours Export), l'UAI fictif est attribué par le back
      return {
        ...etablissement,
        pays: this.paysLibelle,
        complement_adresse: "",
        region: "",
        telephone: "",
      };
    },
    /**
     * Retourne l'option dont la valeur correspond, pour les selects du formulaire d'établissement.
     */
    getOption(options, value) {
      return options.find((option) => option.value === value) || null;
    },
    /**
     * Pays de facturation proposé par défaut selon le profil et le pays du catalogue public.
     */
    getFacturationPaysParDefaut(profil) {
      if (this.isExport) {
        return this.paysLibelle;
      }

      return profil?.zone === "france" ? "France" : "";
    },
    /**
     * Met à jour un champ d'établissement en mode saisie manuelle.
     */
    updateManualEtablissementField(field, value) {
      if (!this.isManualEtablissement) {
        return;
      }

      const normalizedValue = typeof value === "string" ? value : `${value || ""}`;
      const updatedEtablissement = {
        ...this.createEmptyManualEtablissement(),
        ...(this.selectedEtablissement || {}),
        [field]: normalizedValue,
      };

      this.selectedEtablissement = updatedEtablissement;
    },
    /**
     * Gère la saisie dans le champ de recherche d'établissement.
     */
    onEtablissementSearchInput(value) {
      if (this.isManualEtablissement) {
        return;
      }

      this.etablissementQuery = value;
      if (!value || value.trim().length === 0) {
        this.selectedEtablissement = null;
      }

      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);

      this.searchDebounceTimer = setTimeout(() => {
        this.searchEtablissements(value);
      }, 350);
    },
    /**
     * Effectue une recherche d'établissements via l'API.
     */
    async searchEtablissements(query) {
      if (this.isManualEtablissement) {
        this.etablissementResults = [];
        return;
      }

      const cleanQuery = (query || "").trim();
      if (cleanQuery.length < this.minEtablissementSearchLength) {
        this.etablissementResults = [];
        return;
      }

      this.isSearchingEtablissements = true;

      try {
        const response = await Api().get("/organisme/recherche_etablissements/", {
          params: { q: cleanQuery },
        });
        const results = this.extractResults(response?.data);
        this.etablissementResults = results.map((item) => this.normalizeEtablissement(item));
      } catch (error) {
        console.error(error)
        try {
          const fallbackResponse = await Api().get(`/organisme/?search=${encodeURIComponent(cleanQuery)}`);
          const fallbackResults = this.extractResults(fallbackResponse?.data);
          this.etablissementResults = fallbackResults.map((item) => this.normalizeEtablissement(item));
        } catch (fallbackError) {
          console.error(fallbackError)
          this.etablissementResults = [];
        }
      } finally {
        this.isSearchingEtablissements = false;
      }
    },
    /**
     * Sélectionne un établissement parmi les résultats de recherche.
     */
    selectEtablissement(etablissement) {
      const normalizedEtablissement = this.normalizeEtablissement(etablissement);

      this.selectedEtablissement = normalizedEtablissement;
      this.etablissementQuery = normalizedEtablissement.nom || normalizedEtablissement.libelle || "";
      this.etablissementResults = [];
    },
    fillFacturationFromEtablissement() {
      if (!this.selectedEtablissement) return;

      this.form.facturation_nom = this.selectedEtablissement.nom || this.selectedEtablissement.libelle || "";
      this.form.facturation_adresse = [
        this.selectedEtablissement.adresse,
        this.selectedEtablissement.complement_adresse,
      ].filter((value) => !this.isFieldEmpty(value)).join(", ");
      this.form.facturation_code_postal = this.selectedEtablissement.code_postal || "";
      this.form.facturation_ville = this.selectedEtablissement.ville || "";
      this.form.facturation_pays = this.selectedEtablissement.pays
        || this.getFacturationPaysParDefaut(this.selectedProfil);

      // À l'export, le numéro d'entreprise de l'établissement (IDE, BCE, NEQ) n'est pas un SIRET
      const etablissementSiret = this.getEtablissementSiretValue(this.selectedEtablissement);
      if (!this.isExport && !this.isFieldEmpty(etablissementSiret)) {
        this.form.facturation_siret = etablissementSiret;
      }
    },
    /**
     * Copie les informations du demandeur vers le contact de facturation.
     */
    copyRequesterToBillingContact() {
      this.form.facturation_contact_nom = this.form.nom || "";
      this.form.facturation_contact_prenom = this.form.prenom || "";
      this.form.facturation_email = this.form.email || "";
      this.form.facturation_contact_salutation = this.form.salutation || "";

      const matchingFonction = this.facturationFonctionOptions.find(
        (opt) => opt.value.toLowerCase() === this.form.fonction?.toLowerCase()
      );
      if (matchingFonction) {
        this.form.facturation_contact_fonction = matchingFonction.value;
      } else if (this.form.fonction) {
        this.form.facturation_contact_fonction = "Autre";
      } else {
        this.form.facturation_contact_fonction = "";
      }
    },
    /**
     * Construit le payload pour les informations de facturation.
     */
    buildFacturationPayload() {
      if (this.facturationDeferred) {
        return null;
      }

      const fonctionContact = this.normalizeInputValue(this.form.facturation_contact_fonction);
      const payload = {
        nom: this.normalizeInputValue(this.form.facturation_nom),
        adresse: this.normalizeInputValue(this.form.facturation_adresse),
        code_postal: this.normalizeInputValue(this.form.facturation_code_postal),
        ville: this.normalizeInputValue(this.form.facturation_ville),
        pays: this.normalizeInputValue(this.form.facturation_pays),
        salutation_contact: this.normalizeInputValue(this.form.facturation_contact_salutation),
        fonction_contact: fonctionContact,
        categorie_contact: this.deduceCategoryFromFonction(fonctionContact),
        prenom_contact: this.normalizeInputValue(this.form.facturation_contact_prenom),
        nom_contact: this.normalizeInputValue(this.form.facturation_contact_nom),
        email: this.normalizeInputValue(this.form.facturation_email),
      };

      if (!this.isExport) {
        payload.siret = this.normalizeInputValue(this.form.facturation_siret);
      }

      if (this.showChorusPaymentOption) {
        payload.paiement_chorus = Boolean(this.form.facturation_paiement_chorus);
      }

      const hasValue = Object.values(payload).some((value) => !this.isFieldEmpty(value));
      return hasValue ? payload : null;
    },
    /**
     * Ferme la modale après la soumission réussie de la demande.
     */
    closeModalAfterDemande() {
      this.$modal.hide("modal_connexion");
    },
    /**
     * Ouvre le client de messagerie pour les demandes hors France.
     */
    openHorsFranceMailto() {
      const subject = (
        this.$t("connexion.hors-france-modal-mail-subject")
        || "Demande hors de France"
      );
      const defaultBody = (
        "Bonjour,\n\n"
        + "Je souhaite effectuer une demande pour un établissement situé hors de France.\n"
        + "Nom de l'établissement :\n"
        + "Ville :\n"
        + "Pays :\n\n"
        + "Merci."
      );
      const body = (
        this.$t("connexion.hors-france-modal-mail-body")
        || defaultBody
      );
      window.location.href = (
        `mailto:${this.contactEmail}`
        + `?subject=${encodeURIComponent(subject)}`
        + `&body=${encodeURIComponent(body)}`
      );
    },
    /**
     * Soumet la demande de création de compte / demande de devis.
     */
    async submitDemande() {
      if (!this.checkTabValid(2)) {
        this.setTab(2);
        return;
      }

      if (this.needsEtablissement && !this.checkTabValid(3)) {
        this.setTab(3);
        return;
      }

      if (!this.facturationDeferred) {
        if (!this.checkTabValid(4)) {
          this.setTab(4);
          return;
        }
        if (!this.checkTabValid(5)) {
          this.setTab(5);
          return;
        }
      }

      if (!this.checkTabValid(6)) {
        this.setTab(6);
        return;
      }

      this.isLoading = true;
      this.panierVideApresSoumission = false;
      const shouldClearPanier = Boolean(this.hasDemandeDevisContext && this.viderPanierApresDemande);

      // Établissement facultatif non renseigné : on n'envoie pas un formulaire de création vide
      const etablissement = this.isManualEtablissement && this.isManualEtablissementEmpty
        ? null
        : this.selectedEtablissement;

      const basePayload = {
        pays_catalogue_public: this.paysCataloguePublic,
        profil: this.selectedProfil.id,
        salutation: this.form.salutation,
        nom: this.form.nom,
        prenom: this.form.prenom,
        email: this.form.email,
        motif: this.form.motif,
        fonction: this.form.fonction,
        categorie: this.form.categorie,
        etablissement,
        informations_facturation: this.buildFacturationPayload(),
        consentement_donnees: this.form.legalConsent,
        consentement_offres: this.form.marketingConsent,
        vider_panier_apres_demande: shouldClearPanier,
      };

      const payload = {
        ...basePayload,
      };

      if (this.demandeCompteContext) {
        payload.contexte_panier_devis = this.demandeCompteContext;
      }

      let typeCta = "compte";
      if (this.hasDemandeDevisContext) {
        typeCta = this.isCommandeCtaProfil && !this.facturationDeferred ? "commande" : "devis";
      }
      payload.type_cta = typeCta;

      try {
        let response;
        try {
          response = await Api().post("/devis_public/create_from_demande_compte/", payload);
        } catch (error) {
          if (this.demandeCompteContext && error?.response?.status === 400) {
            response = await Api().post("/devis_public/create_from_demande_compte/", basePayload);
          } else {
            throw error;
          }
        }

        if (shouldClearPanier) {
          try {
            await Api().post("/session_panier/clear/");
            this.panierVideApresSoumission = true;
            setHideModalImportPanier(false);
            this.$store.dispatch("setPanier");
          } catch {
            this.$toast.error({
              title: this.$t("connexion.demande-panier-vidage-erreur"),
            });
          }
        }

        this.$toast.success({
          title: this.$t("connexion.demande-envoyee"),
        });
        this.$modal.hide("modal_connexion");
        this.$router.push(response.data.consultation_path);
      } catch {
        this.$toast.error({
          title: this.$t("connexion.demande-erreur"),
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss">
@use "@/assets/styles/components/modals/modal_connexion.scss";
@use "@lde/core_lde_vue/dist/assets/styles/_variables.scss" as var;

.facturation-autofill-btn,
.facturation-use-requester-btn {
  align-self: flex-start;
  margin-bottom: var.$space-tiny;
}

.facturation-use-requester-wrapper {
  margin-bottom: var(--space-tiny, 1rem);
}

.etablissement-reconnu-toast {
  margin-bottom: 0.75rem;
  width: 100%;

  .contact-gestionnaire { margin-top: 0.4rem; }
}
</style>
